import React, { useState, useEffect } from 'react';
import { useStateValue } from './StateProvider';
import StatementAccount from "./StatementAccount";
import { findNearestExchangeRate } from './Utils/currency.js';
import moment from 'moment';

import { getCashAccounts } from './Utils/cashaccounts.js';
import { getClientAccounts } from './Utils/clients.js';
import { getIncomeAccounts } from './Utils/incomeaccounts.js';
import { getExpenseAccounts } from './Utils/expenseaccounts.js';
import { getAssetAccounts } from './Utils/assetaccounts.js';
import { getSupplierAccounts } from './Utils/suppliers.js';
import { getLiabilityAccounts } from './Utils/liabilityaccounts.js';
import { getEquityAccounts } from './Utils/equityaccounts.js';
import { getStaffAccounts } from './Utils/staff.js';
import { getContracts } from './Utils/contracts.js';

import Title from './Title';

import './Summary.css';

function Summary({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const clientsdata = getClientAccounts(project);
    const cashdata = getCashAccounts(project);
    const assetdata = getAssetAccounts(project);
    const liabilitydata = getLiabilityAccounts(project);
    const supplierdata = getSupplierAccounts(project);
    const staffdata = getStaffAccounts(project);
    const equitydata = getEquityAccounts(project);
    const incomedata = getIncomeAccounts(project);
    const expensedata = getExpenseAccounts(project);
    const contractsdata = getContracts(project);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleAccountStatement = (type, account) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <StatementAccount state={state} setState={setState} type={type} account={account} />, modaltype: 'large', modaltitle: 'Statement of Account' }));
    };

    const calculatetotal = (items, exchangerates = [], selectedcurrencyid = '') => {
        let total = 0;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            let balance = parseFloat(item.balance) || 0;

            if (item.currencyid !== selectedcurrencyid) {
                const rateData = findNearestExchangeRate(
                    exchangerates,
                    item.ts || Date.now(),
                    item.currencyid,
                    selectedcurrencyid
                );

                const rate = rateData ? rateData.exchangerate : 1;
                balance = balance * rate;
            }

            total += balance;
        }

        return total;
    };

    function CashAccounts(data, title) {
        const totalbalance = calculatetotal(data.filter(c => c.balance != 0), project.exchangerates, project.basecurrency);

        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={2}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data.filter(client => client.balance != 0).map((client, index) => {
                                return (
                                    <tr key={'client' + index}>
                                        <td><div>{client.name}</div></td>
                                        <td>
                                            <div
                                                className='summary_balance'
                                                onClick={() =>
                                                    handleAccountStatement(
                                                        "cashaccounts",
                                                        {
                                                            id: client.id,
                                                            name: client.name,
                                                            from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                            to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                            subaccountid: '',
                                                            currencyid: project.basecurrency,
                                                        }
                                                    )
                                                }>
                                                {client.currencysymbol + ' ' + client.balance.toLocaleString('en-US')}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>
                                {(project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '') + ' ' + totalbalance.toLocaleString('en-US')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    function ClientAccounts(data, title) {
        const totalbalance = calculatetotal(data.filter(c => c.balance != 0), project.exchangerates, project.basecurrency);

        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={2}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data
                                .filter(data => data.balance != 0)
                                .map((client, index) => {
                                    return (
                                        <tr key={'client' + index}>
                                            <td><div>{client.name}</div></td>
                                            <td>
                                                <div
                                                    className='summary_balance'
                                                    onClick={() =>
                                                        handleAccountStatement(
                                                            "clients",
                                                            {
                                                                id: client.id,
                                                                name: client.name,
                                                                from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                                to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                                subaccountid: '',
                                                                currencyid: client.currencyid,
                                                            }
                                                        )
                                                    }
                                                >
                                                    {client.currencysymbol + ' ' + client.balance.toLocaleString('en-US')}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>
                                {(project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '') + ' ' + totalbalance.toLocaleString('en-US')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    function AssetAccounts(data, title) {
        const totalbalance = calculatetotal(data.filter(c => c.balance != 0), project.exchangerates, project.basecurrency);

        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={2}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data.map((asset, index) => {
                                return (
                                    <tr key={'asset' + index}>
                                        <td><div>{asset.name}</div></td>
                                        <td>
                                            <div
                                                className='summary_balance'
                                                onClick={() =>
                                                    handleAccountStatement(
                                                        "assetaccounts",
                                                        {
                                                            id: asset.id,
                                                            name: asset.name,
                                                            from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                            to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                            subaccountid: '',
                                                            currencyid: asset.currencyid,
                                                        }
                                                    )
                                                }>
                                                {asset.currencysymbol + ' ' + asset.balance.toLocaleString('en-US')}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>
                                {(project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '') + ' ' + totalbalance.toLocaleString('en-US')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    function Contracts(data, title) {
        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={6}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data.map((contract, index) => {
                                let currencySymbol = project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '';
                                let expiryDate = moment.unix(contract.to).format('D/MMM/YYYY');
                                const now = moment().unix();
                                const expiry = contract.to;
                                const isExpired = expiry < now;
                                const isNearExpiry = !isExpired && expiry - now <= 7 * 24 * 60 * 60;

                                let rowClass = "";
                                if (isExpired) {
                                    rowClass = "summary_contractsexpiry";
                                } else if (isNearExpiry) {
                                    rowClass = "summary_contractsnearexpiry";
                                }
                                return (
                                    <tr key={'data' + index} className={rowClass}>
                                        <td><div>{contract.accountname}</div></td>
                                        <td><div>{contract.name}</div></td>
                                        <td><div>{expiryDate}</div></td>
                                        <td><div>{contract.status}</div></td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                </table>
            </div>
        )
    }

    function IncomeAccounts(data, title) {
        const totalbalance = calculatetotal(data.filter(c => c.balance != 0), project.exchangerates, project.basecurrency);

        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={2}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data.map((account, index) => {
                                if (account.tabletype === 'group') {
                                    return (
                                        <tr key={'account' + index}>
                                            <td><div className='summary_incomegroup'>{account.name}</div></td>
                                            <td></td>
                                        </tr>
                                    );
                                }
                                if (account.tabletype === 'account') {
                                    return (
                                        <tr key={'account' + index}>
                                            <td><div className='summary_incomeaccount'>{account.name}</div></td>
                                            <td>
                                                <div
                                                    className='summary_balance'
                                                    onClick={() =>
                                                        handleAccountStatement(
                                                            account.type,
                                                            {
                                                                id: account.id,
                                                                name: account.name,
                                                                from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                                to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                                subaccountid: '',
                                                                currencyid: account.currencyid
                                                            }
                                                        )
                                                    }>
                                                    {account.currencysymbol + ' ' + account.balance.toLocaleString('en-US')}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                            })
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>
                                {(project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '') + ' ' + totalbalance.toLocaleString('en-US')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    function ExpenseAccounts(data, title) {
        const totalbalance = calculatetotal(data.filter(c => c.balance != 0), project.exchangerates, project.basecurrency);

        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={2}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data
                                .filter(account => account.balance != 0)
                                .map((account, index) => {
                                    if (account.tabletype === 'group') {
                                        return (
                                            <tr key={'account' + index}>
                                                <td><div className='summary_group'>{account.name}</div></td>
                                                <td></td>
                                            </tr>
                                        );
                                    }
                                    if (account.tabletype === 'account') {
                                        return (
                                            <tr key={'account' + index}>
                                                <td><div className='summary_account'>{account.name}</div></td>
                                                <td>
                                                    <div
                                                        className='summary_balance'
                                                        onClick={() =>
                                                            handleAccountStatement(
                                                                account.type,
                                                                {
                                                                    id: account.id,
                                                                    name: account.name,
                                                                    from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                                    to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                                    subaccountid: '',
                                                                    currencyid: account.currencyid
                                                                }
                                                            )
                                                        }>
                                                        {account.currencysymbol + ' ' + account.balance.toLocaleString('en-US')}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                })
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>
                                {(project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '') + ' ' + totalbalance.toLocaleString('en-US')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    function EquityAccounts(data, title) {
        const totalbalance = calculatetotal(data.filter(c => c.balance != 0), project.exchangerates, project.basecurrency);

        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={2}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data.map((equity, index) => {
                                return (
                                    <tr key={'equity' + index}>
                                        <td><div>{equity.name}</div></td>
                                        <td>
                                            <div
                                                className='summary_balance'
                                                onClick={() =>
                                                    handleAccountStatement(
                                                        "equityaccounts",
                                                        {
                                                            id: equity.id,
                                                            name: equity.name,
                                                            from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                            to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                            subaccountid: '',
                                                            currencyid: equity.currencyid,
                                                        }
                                                    )
                                                }>
                                                {equity.currencysymbol + ' ' + equity.balance.toLocaleString('en-US')}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>
                                {(project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '') + ' ' + totalbalance.toLocaleString('en-US')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    function LiabilityAccounts(data, title) {
        const totalbalance = calculatetotal(data.filter(c => c.balance != 0), project.exchangerates, project.basecurrency);

        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={2}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data
                                .filter(data => data.balance != 0)
                                .map((liability, index) => {
                                    return (
                                        <tr key={'liability' + index}>
                                            <td><div>{liability.name}</div></td>
                                            <td>
                                                <div
                                                    className='summary_balance'
                                                    onClick={() =>
                                                        handleAccountStatement(
                                                            "liabilityaccounts",
                                                            {
                                                                id: liability.id,
                                                                name: liability.name,
                                                                from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                                to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                                subaccountid: '',
                                                                currencyid: liability.currencyid,
                                                            }
                                                        )
                                                    }>
                                                    {liability.currencysymbol + ' ' + liability.balance.toLocaleString('en-US')}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>
                                {(project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '') + ' ' + totalbalance.toLocaleString('en-US')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    function SupplierAccounts(data, title) {
        const totalbalance = calculatetotal(data.filter(c => c.balance != 0), project.exchangerates, project.basecurrency);

        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={2}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data
                                .filter(data => data.balance != 0)
                                .map((liability, index) => {
                                    return (
                                        <tr key={'liability' + index}>
                                            <td><div>{liability.name}</div></td>
                                            <td>
                                                <div
                                                    className='summary_balance'
                                                    onClick={() =>
                                                        handleAccountStatement(
                                                            "suppliers",
                                                            {
                                                                id: liability.id,
                                                                name: liability.name,
                                                                from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                                to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                                subaccountid: '',
                                                                currencyid: liability.currencyid,
                                                            }
                                                        )
                                                    }>
                                                    {liability.currencysymbol + ' ' + liability.balance.toLocaleString('en-US')}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>
                                {(project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '') + ' ' + totalbalance.toLocaleString('en-US')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    function StaffAccounts(data, title) {
        const totalbalance = calculatetotal(data.filter(c => c.balance != 0), project.exchangerates, project.basecurrency);

        return (
            <div className='summary_tablecontainer'>
                <table className='summary_table'>
                    <thead>
                        <tr>
                            <th className="summary_tableheader" colSpan={2}>
                                {title}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data
                                .filter(data => data.balance != 0)
                                .map((staffmember, index) => {
                                    return (
                                        <tr key={'staffmember' + index}>
                                            <td><div>{staffmember.name}</div></td>
                                            <td>
                                                <div
                                                    className='summary_balance'
                                                    onClick={() =>
                                                        handleAccountStatement(
                                                            "staff",
                                                            {
                                                                id: staffmember.id,
                                                                name: staffmember.name,
                                                                from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                                to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                                subaccountid: '',
                                                                currencyid: staffmember.currencyid,
                                                            }
                                                        )
                                                    }>
                                                    {staffmember.currencysymbol + ' ' + staffmember.balance.toLocaleString('en-US')}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td>
                                {(project.currencies.find(c => c.id == project.basecurrency)?.symbol ?? '') + ' ' + totalbalance.toLocaleString('en-US')}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    return (
        <div className='summary'>
            <Title text='Summary' />

            <div className='summary_row'>
                <div className='summary_column'>
                    <div className='summary_sectiontitle'>Assets</div>
                    {CashAccounts(cashdata, 'Cash & Bank Accounts')}
                    {ClientAccounts(clientsdata, 'Clients')}
                    {AssetAccounts(assetdata, 'Assets')}
                </div>
                <div className='summary_column'>
                    <div className='summary_sectiontitle'>Contracts</div>
                    {Contracts(contractsdata, 'Contracts')}
                </div>
            </div>

            <div className='summary_row'>
                <div className='summary_column'>
                    <div className='summary_sectiontitle'>Liabilities</div>
                    {LiabilityAccounts(liabilitydata, 'Liabilities')}
                    {SupplierAccounts(supplierdata, 'Suppliers')}
                    {StaffAccounts(staffdata, 'Staff')}
                </div>

                <div className='summary_column'>
                    <div className='summary_sectiontitle'>Profit & Loss</div>
                    {IncomeAccounts(incomedata, 'Income')}
                </div>
            </div>

            <div className='summary_row'>
                <div className='summary_column'>
                    <div className='summary_sectiontitle'>Equity</div>
                    {EquityAccounts(equitydata, 'Equity')}
                </div>
                <div className='summary_column'>
                    {ExpenseAccounts(expensedata, 'Expenses')}
                </div>
            </div>
        </div>
    );
}

export default Summary;